import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import * as yup from "yup";

import { useUpdateGpInternalEstimatesForDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import {
  Currency,
  type Currency as CurrencyType,
} from "@vega-api/generated/types/currency";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { toMoneyPayload } from "../utils/moneyUtils";

import { ModalButtonGroup } from "./ModalButtonGroup";

type EditIndicativeOrderModalProps = {
  dealOpportunityId: string;
  dealId: string;
  dealCurrency?: CurrencyType;
  isOpen: boolean;
  onClose: () => void;
  initialValues?: {
    gpIndicativeOrder?: CoreMoney;
    estimatedTicketSizeMin?: CoreMoney;
    estimatedTicketSizeMax?: CoreMoney;
  };
};

type FormValues = {
  gpIndicativeOrder: CoreMoney;
};

const validationSchema = yup.object({
  gpIndicativeOrder: yup.object({
    amount: yup
      .number()
      .required(m.coInvestments_gpIndicativeOrder_modal_required())
      .min(0, m.coInvestments_gpIndicativeOrder_modal_greaterThanZero()),
    currency: yup.string().required(),
  }),
});

export const EditIndicativeOrderModal = ({
  dealOpportunityId,
  dealId,
  dealCurrency = Currency.USD,
  isOpen,
  onClose,
  initialValues,
}: EditIndicativeOrderModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { mutate: updateGpInternalEstimates, isPending } =
    useUpdateGpInternalEstimatesForDealOpportunity({
      mutation: {
        onSuccess: () => {
          showToast({
            variant: "success",
            title: m.coInvestments_gpIndicativeOrder_success(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
          onClose();
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_gpIndicativeOrder_error(),
          });
        },
      },
    });

  const getInitialValues = (): FormValues => ({
    gpIndicativeOrder: initialValues?.gpIndicativeOrder ?? {
      amount: 0,
      currency: dealCurrency,
    },
  });

  const handleSubmit = (values: FormValues) => {
    updateGpInternalEstimates({
      dealOpportunityId,
      data: {
        gpIndicativeOrder: toMoneyPayload(values.gpIndicativeOrder),
        estimatedTicketSizeMin: toMoneyPayload(
          initialValues?.estimatedTicketSizeMin,
        ),
        estimatedTicketSizeMax: toMoneyPayload(
          initialValues?.estimatedTicketSizeMax,
        ),
      },
    });
  };

  return (
    <ActionaryModal
      dataTestId="gp-indicative-order-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_gpIndicativeOrder_modal_title()}
    >
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form className="space-y-6">
          <MoneyField
            allowAbbreviations
            currency={dealCurrency}
            isRequired
            label={m.formFields_amount()}
            name="gpIndicativeOrder"
          />

          <ModalButtonGroup
            cancelTestId="cancel-gp-indicative-order-button"
            confirmTestId="confirm-gp-indicative-order-button"
            isSubmitting={isPending}
            onCancel={onClose}
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
